/**
 * A hook for making ag-grid containers responsive while maintaining ag-grid's need for a fixed height.
 * This hook dynamically calculates and adjusts the container height based on the available space.
 */
import { MobileAccountsAgGrid } from "./mobile_accounts_ag_grid_hook"

// Reuse the mobile table controls and add content-based height.
export const AgGridResponsive = {
  ...MobileAccountsAgGrid,

  /**
   * Override the mounted method to add responsive behavior
   */
  mounted() {
    // Initialize the grid and its mobile viewport behavior first.
    MobileAccountsAgGrid.mounted.call(this)

    // Set up the initial height calculation after grid is initialized
    setTimeout(() => {
      this.updateContainerHeight()
    }, 200)

    // Set up the initial height and add resize listener
    this.onResize = this.handleResize.bind(this)
    window.addEventListener("resize", this.onResize)

    // Create a MutationObserver to watch for DOM changes that might affect layout
    this.observer = new MutationObserver(this.handleDOMChanges.bind(this))
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    })
  },

  /**
   * Override the updated method to call parent and update height
   */
  updated() {
    // Call the parent updated method
    MobileAccountsAgGrid.updated.call(this)

    // Update the container height after data changes
    setTimeout(() => {
      this.updateContainerHeight()
    }, 50)
  },

  /**
   * Override the destroyed method to remove event listeners
   */
  destroyed() {
    window.removeEventListener("resize", this.onResize)
    if (this.observer) {
      this.observer.disconnect()
    }

    // Remove the mobile viewport listener too.
    MobileAccountsAgGrid.destroyed.call(this)
  },

  /**
   * Handle window resize events
   */
  handleResize() {
    this.updateContainerHeight()
  },

  /**
   * Handle DOM changes that might affect layout
   */
  handleDOMChanges(mutations) {
    // Check if any of the mutations affect our parent elements
    const shouldUpdate = mutations.some((mutation) => {
      // Check if the mutation target is our element or a parent
      let node = this.el
      while (node) {
        if (node === mutation.target) return true
        node = node.parentNode
      }
      return false
    })

    if (shouldUpdate) {
      this.updateContainerHeight()
    }
  },

  /**
   * Calculate and update the container height based on visible content
   */
  updateContainerHeight() {
    const container = this.el
    if (!container || !this.api) return

    // Get the number of rows in the grid
    const rowCount = this.api.getDisplayedRowCount()

    // Get the row height (from gridOptions or default)
    const rowHeight = this.gridOptions.rowHeight || 50

    // Get header height
    const headerHeight = this.gridOptions.headerHeight || 50

    // Calculate the exact height needed to display all rows plus header
    // Add a small buffer (10px) to prevent occasional cut-off
    const contentHeight = rowCount * rowHeight + headerHeight + 10

    // Set a reasonable maximum height (adjust as needed)
    const maxHeight = 1200

    // Set the height, but cap it at maxHeight
    const newHeight = Math.min(contentHeight, maxHeight)

    // Set the height directly on the element
    container.style.height = `${newHeight}px`

    // Keep readable column widths and horizontal scrolling on phones, matching
    // the other mobile tables instead of squeezing every column into the viewport.
    if (!window.matchMedia("(max-width: 639px)").matches) {
      this.api.sizeColumnsToFit()
    }
  },
}
